/* eslint-disable jsx-a11y/no-autofocus */
/* eslint-disable prefer-destructuring */
/* eslint-disable import/no-extraneous-dependencies */
import MDBox from "components/Basics/MDBox";
import lod_ from "lodash";
import React, { useState } from "react";
import { getLabel } from "helpers/dictionary";
import { DataGrid, GridFooterContainer } from "@mui/x-data-grid";
import { createDictionarySkeleton } from "helpers/form";
import MDButton from "components/Basics/MDButton";
import { Icon, IconButton } from "@mui/material";
import { useSelector } from "react-redux";
import { selectCurrentProfile } from "redux-react/reducers/profileReducer";

/**
 * Custom footer for the DataGrid to include add and delete buttons
 * @param {function} onDeleteSelected - Function to call when delete button is clicked
 * @param {function} handleAddRow - Function to call when add button is clicked
 * @param {boolean} disableDelete - Whether to disable the delete button
 * @returns JSX.Element
 */
const CustomFooter = ({ onDeleteSelected, handleAddRow, disableDelete, disabled }) => {
	return (
		<GridFooterContainer>
			{!disabled && (
				<MDBox display="flex" alignItems="center" p={1}>
					<MDButton
						iconOnly
						sx={{ mr: 1 }}
						variant="gradient"
						color="dark"
						size="small"
						onClick={handleAddRow}
					>
						<Icon>add</Icon>
					</MDButton>
					<MDButton
						iconOnly
						disabled={disableDelete}
						variant="outlined"
						color="error"
						size="small"
						onClick={onDeleteSelected}
					>
						<Icon>delete</Icon>
					</MDButton>
				</MDBox>
			)}
			<MDBox sx={{ flex: 1 }} />
		</GridFooterContainer>
	);
};

/**
 * Input for array of objects, displayed as a table with add/remove row functionality
 * @param {object} dictionary - The dictionary defining the structure of the objects in the array
 * @param {string} path - The path to the array in the form values
 * @param {array} values - The current values of the array
 * @param {function} handleChange - Function to call when the array values change
 * @returns JSX.Element
 */
const LittleArrayForm = ({ dictionary, path, values, handleChange, disabled }) => {
	const profile = useSelector(selectCurrentProfile);

	const [selectedRowsIDs, setSelectedRowsIDs] = useState({
		type: "include",
		ids: new Set()
	});

	const getType = type => {
		switch (type) {
			case "date":
			case "datetime":
				return "date";
			case "boolean":
				return "boolean";
			case "number":
				return "number";
			default:
				return "string";
		}
	};

	const columns = React.useMemo(() => {
		const cols = [];
		Object.entries(dictionary).forEach(([key, item]) => {
			if (item.display !== false) {
				const column = {
					field: key,
					headerName: getLabel(item.label, "fr") || key,
					type: getType(item.type),
					editable: disabled ? false : (item.actions?.edit ?? true),
					flex: 1,
					renderCell: params => {
						const type = params?.colDef?.type || "string";
						let value = params.value;

						switch (type) {
							case "date":
							case "datetime":
								value = new Date(value).toLocaleDateString(profile?.language || "fr-FR");
								break;
							case "boolean":
								value = Boolean(value);
								break;
							case "number":
								value = parseFloat(value);
								break;
							default:
								break;
						}

						return value;
					}
				};

				if (item.type === "string") {
					column.renderEditCell = params => (
						<textarea
							style={{ width: "100%", minHeight: 60, fontSize: "0.6rem" }}
							value={params.value ?? ""}
							onChange={event =>
								params.api.setEditCellValue({
									id: params.id,
									field: params.field,
									value: event.target.value
								})
							}
							autoFocus
						/>
					);
				}

				cols.push(column);
			}
		});
		return cols;
	}, [dictionary, disabled]);

	const rows = React.useMemo(() => {
		const rowsData = [];
		(values || []).forEach((value, index) => {
			rowsData.push({
				id: index,
				...value
			});
		});
		return rowsData;
	}, [values, columns]);

	const paginationModel = React.useMemo(
		() => ({
			page: 0,
			pageSize: Math.max(rows.length, 1)
		}),
		[rows.length]
	);

	const onChangeController = (index, newRow) => {
		const newValues = lod_.cloneDeep(values);
		newValues[index] = lod_.omit(newRow, "id");
		handleChange(path, newValues);
	};

	const handleSelectionChange = selectionModel => {
		setSelectedRowsIDs(selectionModel);
	};

	const handleDeleteSelected = () => {
		if (selectedRowsIDs.ids.size === 0) return;
		const idsToDelete = Array.from(selectedRowsIDs.ids);
		const newValues = values.filter((_, i) => !idsToDelete.includes(i));
		handleChange(path, newValues);
		setSelectedRowsIDs({
			type: "include",
			ids: new Set()
		});
	};

	const handleAddRow = () => {
		const newRow = createDictionarySkeleton(dictionary, {});
		const newValues = [...(values || []), newRow];
		handleChange(path, newValues);
	};

	return (
		<MDBox>
			<DataGrid
				processRowUpdate={(newRow, oldRow) => {
					const index = newRow.id;
					onChangeController(index, newRow);
					return newRow;
				}}
				rows={rows}
				columns={columns}
				paginationModel={paginationModel}
				onPaginationModelChange={() => {}}
				disableRowSelectionOnClick
				density="compact"
				disableColumnFilter
				disableColumnMenu
				disableColumnSelector
				checkboxSelection={!disabled}
				rowSelectionModel={selectedRowsIDs}
				onRowSelectionModelChange={handleSelectionChange}
				getRowHeight={() => "auto"}
				slots={{
					footer: CustomFooter
				}}
				slotProps={{
					footer: {
						onDeleteSelected: handleDeleteSelected,
						handleAddRow,
						disableDelete: selectedRowsIDs.ids.size === 0,
						disabled
					}
				}}
				sx={{
					border: "1px solid rgba(224, 224, 224, 1)",
					// Disable horizontal scrolling
					"& .MuiDataGrid-virtualScroller": {
						overflowX: "hidden"
					},
					// Hide the presentation div (match nested elements, not only direct children)
					"& .MuiDataGrid-virtualScrollerContent div[role='presentation']": {
						display: "none"
					},
					// Ensure columns fit content
					"& .MuiDataGrid-columnHeaders": {
						width: "fit-content !important"
					},
					"& .MuiDataGrid-virtualScrollerRenderZone": {
						width: "fit-content !important"
					},
					// Remove width constraints from checkbox column
					"& .MuiDataGrid-cellCheckbox, & .MuiDataGrid-columnHeaderCheckbox": {
						width: "40px !important"
					},
					// Allow rows to auto-size based on content
					"& .MuiDataGrid-row": {
						maxHeight: "none !important"
					},
					"& .MuiDataGrid-cell": {
						maxHeight: "none !important",
						overflow: "visible",
						whiteSpace: "normal",
						lineHeight: "1.2 !important",
						display: "flex",
						alignItems: "center",
						padding: "2px",
						paddingTop: "4px",
						paddingBottom: "4px",
						fontSize: "0.6rem",
						wordWrap: "break-word",
						borderLeft: "1px solid rgba(224, 224, 224, 1)"
					},
					// Style for cells in edit mode
					"& .MuiDataGrid-cell--editing": {
						maxHeight: "none !important",
						overflow: "visible",
						whiteSpace: "normal",
						lineHeight: "1.2 !important",
						padding: "2px",
						paddingTop: "4px",
						paddingBottom: "4px",
						borderBottom: "1px solid rgba(224, 224, 224, 1)"
					},
					// Style for input elements in edit mode
					"& .MuiDataGrid-cell--editing .MuiInputBase-root": {
						fontSize: "0.6rem",
						lineHeight: "1.2"
					},
					"& .MuiDataGrid-cell--editing .MuiInputBase-input": {
						fontSize: "0.7rem",
						lineHeight: "1.2",
						padding: "2px 4px"
					},
					"& .MuiDataGrid-columnHeader": {
						maxHeight: "none !important",
						borderBottom: "2px solid rgba(224, 224, 224, 1)"
					},
					"& .MuiDataGrid-columnHeaderTitle": {
						fontSize: "0.6rem",
						fontWeight: "600",
						whiteSpace: "normal",
						lineHeight: "1.2",
						overflow: "visible"
					},
					"& .MuiDataGrid-footerContainer": {
						minHeight: "0px",
						borderTop: "1px solid rgba(224, 224, 224, 1)"
					},
					"& .MuiTablePagination-displayedRows": {
						fontSize: "0.75rem"
					},
					"& .MuiTablePaginationActions-root .MuiButtonBase-root": {
						fontSize: "1.3rem"
					},
					"& .MuiTablePagination-root .MuiTablePagination-toolbar": {
						minHeight: "0px"
					}
				}}
			/>
		</MDBox>
	);
};

export default LittleArrayForm;
